import React, { useState } from "react";
import styled from "styled-components";
import _ from "underscore";
import { Colors } from "../../config/Constants";
import { Pagination } from "../Pagination";

interface Props {
    columns: (
        | {
            path: string;
            label: string | null;
            key: string | null;
            // eslint-disable-next-line no-empty-pattern
            content: ({ }) => Element | null;
        }
        | any
    )[];
    data: {}[];
    onRowClick?: (item: any) => void;
    isTableDataLoading?: boolean;
    currentPage: number;
    title: string;
    totalData: number;
    rowsPerPage: number;
    onPageChange: (page: number) => void;
    hidePagination?: boolean;
}

const ListWithPaginationTicket = ({
    columns,
    data,
    onRowClick,
    isTableDataLoading,
    totalData,
    currentPage,
    rowsPerPage,
    onPageChange,
    hidePagination = false,
    title,
}: Props) => {
    const [showMore, setShowMore] = useState<number | null>(null); // Track which item is expanded

    const renderCell = (
        item: {},
        column: {
            path: string;
            label: string | null;
            key: string | null;
            content: ({ }) => Element | null;
        }
    ): Element | React.ReactNode | null | string | any => {
        if (column.content) return column.content(item);
        return _.get(item, column.path);
    };

    return (
        <div>
            {!isTableDataLoading ? (
                <>
                    {data?.length > 0 ? (
                        <ListContainer>
                            {data?.map((dataItem: any, idx: number) => (
                                <div
                                    className={idx % 2 === 0 ? "even" : "odd"}
                                    onClick={() => onRowClick && onRowClick(dataItem)}
                                    key={idx}
                                >
                                    {columns.slice(0, 6).map((column: any, id: number) => (
                                        <Column
                                            className={id % 2 === 0 ? "evenColumn" : "oddColumn"}
                                            key={id}
                                        >
                                            <Label>{column.label}</Label>
                                            <Value>{renderCell(dataItem, column)}</Value>
                                        </Column>
                                    ))}

                                    {/* Conditionally render additional details */}
                                    {showMore === idx && (
                                        <>
                                            {columns.slice(6).map((column: any, id: number) => (
                                                <Column
                                                    className={id % 2 === 0 ? "evenColumn" : "oddColumn"}
                                                    key={`extra-${id}`}
                                                >
                                                    <Label>{column.label}</Label>
                                                    <Value>{renderCell(dataItem, column)}</Value>
                                                </Column>
                                            ))}
                                        </>
                                    )}


                                    <ShowMoreButton
                                        style={{
                                            textAlign: showMore === idx ? 'right' : 'left',
                                        }}
                                        onClick={() => setShowMore(showMore === idx ? null : idx)}>
                                        {showMore === idx ? "Show Less Details" : "Show More Details"}
                                    </ShowMoreButton>
                                </div>
                            ))}
                        </ListContainer>
                    ) : (
                        <EmptyState>{"No Records Found"}</EmptyState>
                    )}
                </>
            ) : (
                <Loading>Loading...</Loading>
            )}
            {data?.length > 0 && !hidePagination && (
                <Pagination
                    onPageChange={onPageChange}
                    isTableDataLoading={isTableDataLoading}
                    totalData={totalData}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalPages={Math.ceil(totalData / rowsPerPage)}
                />
            )}
        </div>
    );
};

// Styled Components

const ListContainer = styled.div`
  overflow: hidden; 
  > .even,
  > .odd {
    padding: 1rem;
    display: grid;
    grid-template-columns: 50% 50%;
    row-gap: 0.5rem;
    margin-bottom: 1rem;
    &.even {
      background: ${Colors.fogWhite}; 
      border-radius: 0.5rem; 
      border: 1px solid ${Colors.cloudyGrey}; 
    }
    &.odd {
      background: ${Colors.white};
      border-radius: 0.5rem;
      border: 1px solid ${Colors.cloudyGrey}; 
    }
    > div {
      display: flex;
      flex-direction: column;
      font-size: 0.75rem;
    }
  }
`;

const Value = styled.span`
  color: ${Colors.black};
  inline-size: 100%;
  overflow-wrap: break-word;
  font-weight: bold;
  font-family: "Poppins-Medium";
  margin-bottom: 1rem;
`;
const Label = styled.span`
  color: ${Colors.eastBay};
  font-weight: 600;
  margin-bottom: 0.25rem;
  font-family: "Poppins-Medium";
`;

const ListHeader = styled.div`
  padding: 1rem 1.5rem;
`;

const Column = styled.div`
  &.evenColumn {
    display: flex;
    justify-content: flex-start;
    text-align: left;
    > span {
      display: flex;
      justify-content: flex-start;
      text-align: left;
    }
  }
  &.oddColumn {
    display: flex;

    justify-content: flex-end;
    text-align: right;
    > span {
      display: flex;

      justify-content: flex-end;
      text-align: right;
    }
  }
`;

const Loading = styled.div`
  display: flex;
  padding: 2rem;
  align-items: center;
  justify-content: center;
  color: ${Colors.silver};
`;

const EmptyState = styled.div`
  width: 100%;
  color: ${Colors.silver};
  text-align: center;
`;

const ShowMoreButton = styled.button`
  background: transparent;
  border: none;
  color: ${Colors.aqua};
  font-size: 0.85rem;
  font-weight: bold;
  cursor: pointer;
  margin-top: 0.5rem;
  /* text-align: left; */
`;

export default ListWithPaginationTicket;
